import Model from '@/store/plugins/orm/Model';
import { TaxRate, ProductCategory } from './ModelRepository';

import ApiService from '@/services/ApiService';
import i18n from '@/i18n';

export class Product extends Model {
    static get entity() {
        return 'products';
    }

    static get PRINTING_SECTOR_BAR() {
        return 'bar';
    }

    static get PRINTING_SECTOR_KITCHEN() {
        return 'kitchen';
    }

    static get PRINTING_SECTOR_SECTOR_1() {
        return 'sector_1';
    }

    static get PRINTING_SECTOR_SECTOR_2() {
        return 'sector_2';
    }

    static get PRINTING_SECTORS() {
        return [
            Product.PRINTING_SECTOR_BAR,
            Product.PRINTING_SECTOR_KITCHEN,
            Product.PRINTING_SECTOR_SECTOR_1,
            Product.PRINTING_SECTOR_SECTOR_2
        ];
    }

    static fields() {
        return {
            id: this.attr(null),
            name: this.attr(null),
            code: this.attr(null),
            barcode: this.attr(null),
            gtin: this.attr(null),
            is_goods: this.attr(null),
            is_measured: this.attr(null),
            is_made: this.attr(null),
            is_sold: this.attr(null),
            category_id: this.attr(null),
            type: this.attr(null),
            printing_sector: this.attr(null),
            selling_price: this.attr('0.0000', value => _.round(value, 4).toFixed(4)),
            tax_rate_id: this.attr(null),
            count: this.attr('0.000', value => _.round(value, 3).toFixed(3)),
            min_count: this.attr(null, value => (value === null ? null : _.round(value, 3).toFixed(3))),
            selling_count: this.attr('0.000', value => _.round(value, 3).toFixed(3)),
            unit_of_measure: this.attr(null),
            unit_of_measure_sold: this.attr(null),
            net_quantity: this.attr('0.000', value => value ? _.round(value, 3).toFixed(3) : null),
            modified_at: this.attr(null),
            updated_at: this.attr(null),

            prices: this.attr([], value => {
                return value.map(item => {
                    return { id: item.id, name: item.name, price: _.round(item.price, 4).toFixed(4) }
                })
            }),

            category: this.belongsTo(ProductCategory, 'category_id'),
            tax_rate: this.belongsTo(TaxRate, 'tax_rate_id')
        };
    }

    /**
     * Fetch new products from server
     */
    static async fetchLatest() {
        const lastUpdatedAt = Product.getLastUpdatedAt();
        const params = { last_updated_at: lastUpdatedAt ? lastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : null };
        const response = await ApiService.get('/products/latest', { params: params });
        Product.insert({ data: response.data.products, freeze: true });
    }

    /**
     * Make a copy of the Product.
     */
    $duplicate() {
        return new Product(this.$toJson());
    }

    /**
     * Lazy Get Tax Rate
     */
    getTaxRate() {
        return TaxRate.find(this.tax_rate_id);
    }

    getCount() {
        return parseFloat(this.count);
    }

    getSellingCount() {
        return parseFloat(this.selling_count);
    }

    getSellingPrice(pricelistId = 0) {
        if(pricelistId) {
            const pricelist = this.prices.find(pricelist => pricelist.id === pricelistId);
            if(pricelist) {
                return Money.parse(pricelist.price);
            }
        }

        return Money.parse(this.selling_price);
    }

    getPriceBeforeTax(pricelistId = 0) {
        const taxRate = this.getTaxRate();

        if (!taxRate) {
            return this.getSellingPrice(pricelistId);
        }

        const reverseCalculation = !!this.$store().getters.tenant.venue.settings.reverse_calculation;

        if (reverseCalculation) {
            return this.getSellingPrice(pricelistId);
        }

        return this.getSellingPrice(pricelistId).divide(1 + taxRate.getRate() / 100);
    }

    getPriceAfterTax(pricelistId = 0) {
        const taxRate = this.getTaxRate();

        if (!taxRate) {
            return this.getSellingPrice(pricelistId);
        }

        const reverseCalculation = !!this.$store().getters.tenant.venue.settings.reverse_calculation;

        if (reverseCalculation) {
            return this.getSellingPrice(pricelistId).multiply(1 + taxRate.getRate() / 100);
        }

        return this.getSellingPrice(pricelistId);
    }

    getSellingValueBeforeTax() {
        return this.getPriceBeforeTax().multiply(this.getCount());
    }

    getSellingValueAfterTax() {
        return this.getPriceAfterTax().multiply(this.getCount());
    }

    getNetQuantity() {
        return this.net_quantity ? parseFloat(this.net_quantity) : null;
    }

    getQuantityUnitPrice() {
        if(!this.getNetQuantity()) return Money.parse(0)
        return this.getPriceAfterTax().divide(this.getNetQuantity())
    }

    static search(query, products = null, limit = 10, disableQuantity = false) {
        if (!query) {
            return {
                products: []
            };
        }

        if (!products) {
            products = Product.all().filter(product => product.is_sold);
        }

        // quantity search
        const isQuantitySearch = !disableQuantity ? /^[0-9]{1,}\.?[0-9]{0,3}\*/.test(query) : false;
        if (isQuantitySearch) {
            return {
                products: [],
                quantity: parseFloat(query.split('*')[0]),
                query: query.split('*')[1]
            };
        }

        // weighted barcode search
        const queryText = query.toLowerCase();
        const barcodePatterns = this.store().getters.tenant.venue.settings.barcode_pattern.split('|');

        for (let i = 0; i < barcodePatterns.length; i++) {
            const barcodePattern = barcodePatterns[i].split(',');
            const minBarcodeLength =
                barcodePattern[0].length + parseInt(barcodePattern[1]) + parseInt(barcodePattern[2]);

            if (queryText.startsWith(barcodePattern[0]) && queryText.length >= minBarcodeLength) {
                const codeInBarcode = queryText.substr(barcodePattern[0].length, parseInt(barcodePattern[1]));

                const product = products.find(product => {
                    const code = product.code ? product.code.toLowerCase().replace(/^0+/, '') : '';
                    return code && code === codeInBarcode.replace(/^0+/, '');
                });

                if (product) {
                    const quantityDecimals = !isNaN(barcodePattern[3]) ? parseInt(barcodePattern[3]) : 3;

                    const quantity =
                        parseFloat(
                            queryText.substr(
                                barcodePattern[0].length + parseInt(barcodePattern[1]),
                                parseInt(barcodePattern[2])
                            )
                        ) / Math.pow(10, quantityDecimals);

                    return {
                        products: [product],
                        quantity: quantity
                    };
                }
            }
        }

        // exact search by name
        let productsMatched = products.filter(product => product.name.toLowerCase() === queryText);
        if (productsMatched.length) {
            return {
                products: productsMatched.splice(0, limit)
            };
        }

        // exact search by barcode
        productsMatched = products.filter(product => product.barcode && product.barcode.toLowerCase() === queryText);
        if (productsMatched.length) {
            return {
                products: productsMatched.splice(0, limit)
            };
        }

        // exact search by code
        productsMatched = products.filter(
            product => product.code && product.code.toLowerCase().replace(/^0+/, '') === queryText.replace(/^0+/, '')
        );
        if (productsMatched.length) {
            return {
                products: productsMatched.splice(0, limit)
            };
        }

        // exact search by gtin
        productsMatched = products.filter(product => product.gtin && product.gtin.toLowerCase() === queryText);
        if (productsMatched.length) {
            return {
                products: productsMatched.splice(0, limit)
            };
        }

        // partial search
        return {
            products: products
                .filter(product => {
                    const name = product.name.toLowerCase();
                    const code = product.code ? product.code.toLowerCase() : '';
                    const barcode = product.barcode ? product.barcode.toLowerCase() : '';
                    const gtin = product.gtin ? product.gtin.toLowerCase() : '';

                    return (
                        name.indexOf(queryText) > -1 ||
                        code.indexOf(queryText) > -1 ||
                        barcode.indexOf(queryText) > -1 ||
                        gtin.indexOf(queryText) > -1
                    );
                })
                .splice(0, limit)
        };
    }

    /**
     * Get last update of products from server
     */
    static getLastUpdatedAt() {
        return Product.all().length
            ? moment(Math.max(...Product.all().filter(p => p.updated_at).map(p => moment(p.updated_at).valueOf())))
            : null;
    }

    getUnitOfMeasure() {
        return i18n.t(`products.unit_of_measure.${this.unit_of_measure}`) !== `products.unit_of_measure.${this.unit_of_measure}`
            ? i18n.t(`products.unit_of_measure.${this.unit_of_measure}`)
            : this.unit_of_measure
    }

    getUnitOfMeasureSold() {
        return i18n.t(`products.unit_of_measure.${this.unit_of_measure_sold}`) !== `products.unit_of_measure.${this.unit_of_measure_sold}`
            ? i18n.t(`products.unit_of_measure.${this.unit_of_measure_sold}`)
            : this.unit_of_measure_sold
    }

    usesInventoryAccounting() {
        if(!this.is_goods || this.is_made || !this.is_sold) return false;
        return !!this.$store().getters.tenant.venue?.settings?.use_inventory_accounting;
    }
}

export default Product;
